import { Affix, Empty } from 'antd';
import React, { FC } from 'react';
import { GoogleMap, GoogleMapsMarker } from '../../../../../components';

type Map = google.maps.Map;
type GoogleMapsApi = typeof google.maps;
type LatLongBounds = google.maps.LatLngBounds;

interface Props {
  profiles: Profile[];
}

interface Profile {
  id: string;
  name: string;
  mapNumber: number;
  location: {
    id: string;
    latitude: number;
    longitude: number;
  };
}

type Place = {
  lat: number;
  lng: number;
  profileId: string;
} & Profile;

const defaultZoomLevel = 10;

// Return map bounds based on list of places
const getMapBounds = (map: Map, maps: GoogleMapsApi, places: Place[]): LatLongBounds => {
  const bounds = new google.maps.LatLngBounds();

  places.forEach(place => {
    bounds.extend(new maps.LatLng(place.lat, place.lng));
  });
  return bounds;
};

// Set zoom level when map contains a single location. This avoids zooming in so
// much that the user has to zoom out to gain any value from the map.
const maybeSetZoomForSingleLocation = (map: Map, places: Place[]): void => {
  if (!places) return;

  if (places.length === 1) {
    map.setZoom(defaultZoomLevel);
  } else if (places.length > 1) {
    const locationIds = places.map(x => x.location.id);
    const uniqueLocationIds = new Set(locationIds);
    if (uniqueLocationIds.size === 1) map.setZoom(defaultZoomLevel);
  }
};

// Re-center map when resizing the window
const bindResizeListener = (map: Map, maps: GoogleMapsApi, bounds: LatLongBounds, places: Place[]): void => {
  maps.event.addDomListenerOnce(map, 'idle', () => {
    maps.event.addDomListener(window, 'resize', () => {
      map.fitBounds(bounds);
      maybeSetZoomForSingleLocation(map, places);
    });
  });
};

// Fit map to its bounds after the api is loaded
const apiIsLoaded = (map: Map, maps: GoogleMapsApi, places: Place[]): void => {
  // Get bounds by our places
  const bounds = getMapBounds(map, maps, places);
  // Fit map to bounds
  map.fitBounds(bounds);
  // Bind the resize listener
  bindResizeListener(map, maps, bounds, places);

  maybeSetZoomForSingleLocation(map, places);
};

const ProfileGoogleMap: FC<Props> = ({ profiles }) => {
  const places = profiles
    .filter(p => p.location && p.location.latitude && p.location.longitude)
    .map(prof => {
      return {
        ...prof,
        lat: prof.location.latitude,
        lng: prof.location.longitude,
        profileId: prof.id,
        name: prof.name,
      };
    });

  return (
    <Affix>
      {places && places.length > 0 ? (
        <GoogleMap
          style={{ height: '400px' }}
          defaultZoom={10}
          defaultCenter={{ lat: 34.0522, lng: 118.2437 }}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({ map, maps }) => apiIsLoaded(map, maps, places)}
        >
          {places.map(place => (
            <GoogleMapsMarker
              key={place.profileId}
              number={place.mapNumber}
              lat={place.lat}
              lng={place.lng}
              label={place.name}
            />
          ))}
        </GoogleMap>
      ) : (
        <div
          style={{
            height: '400px',
            border: '1px solid #dae1e7',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Empty description="No Map Data" />
        </div>
      )}
    </Affix>
  );
};

export default ProfileGoogleMap;
